import { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Button, Chip, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import { api, authenticatedFetch } from '../utils/api';

function TodayCheckinStatus() {
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkTodayStatus();
  }, []);

  const checkTodayStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(api.checkinToday, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasCheckedInToday(data.hasCheckedInToday);
        setTodayCheckin(data.todayCheckin);
      }
    } catch (error) {
      console.error('Error checking today\'s status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMoodEmoji = (mood) => {
    const emojis = { 'Excellent': '😄', 'Good': '🙂', 'Okay': '😐', 'Poor': '😔', 'Terrible': '😢' };
    return emojis[mood] || '😐';
  };

  const getProgressColor = (value, max = 10) => {
    const percentage = (value / max) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'primary';
    if (percentage >= 40) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography>Loading today's status...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!hasCheckedInToday) {
    return (
      <Card sx={{ 
        mb: 3, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <AddCircleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
          <Typography variant="h6" gutterBottom>
            Ready for today's check-in?
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
            Start your wellness journey with a quick health check-in
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/checkin')}
            sx={{ 
              bgcolor: 'white', 
              color: '#667eea',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
            }}
          >
            Start Check-in
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">
              Today's Check-in Complete
            </Typography>
          </Box>
          <Chip 
            label="Completed" 
            color="success" 
            size="small"
          />
        </Box>

        {todayCheckin && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Mood */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {getMoodEmoji(todayCheckin.mood)} Mood
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {todayCheckin.mood}
                </Typography>
              </Box>
            </Box>

            {/* Energy */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  ⚡ Energy Level
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {todayCheckin.energy}/10
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(todayCheckin.energy / 10) * 100} 
                color={getProgressColor(todayCheckin.energy)}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>

            {/* Sleep */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  😴 Sleep Hours
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {todayCheckin.sleepHours}h
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(todayCheckin.sleepHours / 8) * 100} 
                color={getProgressColor(todayCheckin.sleepHours, 8)}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>

            {/* Additional metrics for detailed check-ins */}
            {todayCheckin.soreness && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    🏃 Soreness Level
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {todayCheckin.soreness}/10
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(todayCheckin.soreness / 10) * 100} 
                  color={getProgressColor(10 - todayCheckin.soreness)} // Inverted for soreness
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}

            {todayCheckin.hydration && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    💧 Hydration
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {todayCheckin.hydration}/10
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(todayCheckin.hydration / 10) * 100} 
                  color={getProgressColor(todayCheckin.hydration)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}

            {/* Timestamp */}
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Last updated: {new Date(todayCheckin.timestamp).toLocaleString()}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate('/checkin')}
                sx={{ flex: 1 }}
              >
                Update Check-in
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default TodayCheckinStatus;
